/**
 * CTRIP-P2 · 携程建档抽屉（样稿 ② 缺项检查 + 推送 / ③ 提交之后的三种样子）。
 * 契约：designs/mockups/channel-center/API_MAP-携程价库直连.md · 后端 routes/ctrip_listings.py
 * 缺一项「提交到携程审核」就是灰的（不拿默认值顶替）；提交成功后才建映射（由 onSubmitted 回调给上架中心做）。
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CTRIP_LISTING_KEY, bindCtripListing, ctripErrorMessage, getCtripListing, offlineCtripListing, precheckCtripListing, resubmitCtripListing, submitCtripListing, unbindCtripListing,
  type CtripListingDetail, type CtripMissing,
} from "@/services/ctripListings";

const GROUPS: { key: CtripMissing["group"]; title: string; okText: string }[] = [
  { key: "product", title: "产品资料（在产品详情补）", okText: "产品资料已齐" },
  { key: "lookup", title: "携程对照表（在对照表点选）", okText: "城市 / 景点 / 酒店都已对照" },
  { key: "profile", title: "携程资料（全公司一份）", okText: "合同号 / 品牌名 / 联系人 / 儿童规则已填" },
];

const STATUS_PILL: Record<string, { text: string; cls: string }> = {
  missing: { text: "未提交", cls: "bg-rose-50 text-rose-600" },
  draft: { text: "未提交", cls: "bg-rose-50 text-rose-600" },
  auditing: { text: "携程审核中", cls: "bg-amber-50 text-amber-600" },
  rejected: { text: "携程驳回", cls: "bg-rose-50 text-rose-600" },
  on_sale: { text: "在售", cls: "bg-emerald-50 text-emerald-600" },
  content_stale: { text: "在售 · 内容改过", cls: "bg-amber-50 text-amber-600" },
  offline: { text: "已下线", cls: "bg-slate-100 text-slate-600" },
};

function fmtTime(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

function Timeline({ status }: { status: string }) {
  const rejected = status === "rejected";
  const steps: { text: string; cls: string }[] = [
    { text: "已提交", cls: "border-emerald-200 bg-emerald-50 text-emerald-600" },
    status === "auditing" ? { text: "携程审核", cls: "border-amber-200 bg-amber-50 font-semibold text-amber-600" }
      : rejected ? { text: "驳回", cls: "border-rose-200 bg-rose-50 font-semibold text-rose-600" }
      : { text: "审核通过", cls: "border-emerald-200 bg-emerald-50 text-emerald-600" },
    status === "on_sale" || status === "content_stale" ? { text: "在售", cls: "border-emerald-200 bg-emerald-50 text-emerald-600" } : { text: "在售", cls: "border-slate-200 bg-white text-slate-400" },
  ];
  return (
    <div className="flex flex-wrap text-[12.5px]" data-testid="ctrip-timeline">
      {steps.map((s, i) => (
        <span key={i} className={`border px-2.5 py-1 ${s.cls} ${i === 0 ? "rounded-l-full" : ""} ${i === steps.length - 1 ? "rounded-r-full" : ""}`}>{s.text}</span>
      ))}
    </div>
  );
}

/** 驳回后的「去补」入口：按携程原话判断缺的是城市 / 景点 / 酒店 → 去对照表对应页；判断不了 → 产品详情（样稿 ③） */
function fixTarget(reason: string | null | undefined, productId: string): { to: string; text: string } {
  const r = reason ?? "";
  if (/酒店/.test(r)) return { to: "/admin/listing/ctrip-lookups?kind=hotel", text: "去对照表补酒店" };
  if (/景点|景区/.test(r)) return { to: "/admin/listing/ctrip-lookups?kind=poi", text: "去对照表补景点" };
  if (/城市/.test(r)) return { to: "/admin/listing/ctrip-lookups?kind=city", text: "去对照表补城市" };
  return { to: `/products/${productId}`, text: "去产品详情" };
}

function Submitted({ d, canManage, optionId, productId, onChanged }: { d: CtripListingDetail; canManage: boolean; optionId: string; productId: string; onChanged: () => void }) {
  const auto = d.auto_listing_enabled;   // CTRIP-P3 · 自动建档开关关着：「重新提交」会 403，所以不显示按钮、给说明（审核回调照常收，状态照常更新）
  const resub = useMutation({ mutationFn: () => resubmitCtripListing(optionId), onSuccess: onChanged });
  const off = useMutation({ mutationFn: () => offlineCtripListing(optionId), onSuccess: onChanged });
  const err = resub.error ?? off.error;
  return (
    <div className="flex flex-col gap-3" data-testid="ctrip-submitted">
      <div className="flex items-center gap-2 text-[13px]">
        <span className="font-semibold text-slate-800">{STATUS_PILL[d.status]?.text ?? d.status}</span>
        {d.ctrip_product_code != null && <span className="font-mono text-[12px] text-slate-500">携程产品号 {d.ctrip_product_code}</span>}
        {d.status === "on_sale" || d.status === "content_stale" ? (
          canManage && (
            <button type="button" disabled={off.isPending} onClick={() => { if (window.confirm("从携程下线后携程搜不到这个产品，确认？")) off.mutate(); }}
              className="ml-auto rounded-lg border border-slate-300 bg-white px-2.5 py-1 text-[12px] text-slate-700 disabled:opacity-50">从携程下线</button>
          )
        ) : null}
      </div>
      <Timeline status={d.status} />
      {d.status === "auditing" && (
        <div className="rounded-[10px] border border-amber-200 bg-amber-50 px-3 py-2 text-[12.5px] text-amber-800">
          {fmtTime(d.submitted_at)} 提交 · 携程审完会自动回来，通过后自动开卖。审核期间改价、余位照常记下，通过那一刻一起推。
        </div>
      )}
      {d.status === "rejected" && (
        <div className="rounded-[10px] border border-rose-200 bg-rose-50 px-3 py-2 text-[12.5px] text-rose-800" data-testid="ctrip-reject-reason">
          <b>携程原话：</b>{d.audit?.reject_reason ?? "（携程没有给原因）"}
          {d.audit?.days ? <span>（班期：{d.audit.days}）</span> : null}
        </div>
      )}
      {d.status === "content_stale" && (
        <div className="rounded-[10px] border border-amber-200 bg-amber-50 px-3 py-2 text-[12.5px] text-amber-800">
          行程 / 图片 / 文案 / 对照改过了。价格、余位一直在自动推；这些内容要重新提交，携程会再审一次，审核期间原产品照常卖。
        </div>
      )}
      {d.audit && d.audit.type !== "Product" && d.audit.result === "Rejected" && (
        <div className="rounded-[10px] border border-rose-200 bg-rose-50 px-3 py-2 text-[12.5px] text-rose-800" data-testid="ctrip-price-reject">
          携程{d.audit.type === "Price" ? "价格" : "库存"}审核被拒：{d.audit.reject_reason}{d.audit.days ? `（班期：${d.audit.days}）` : ""}
        </div>
      )}
      {(d.status === "rejected" || d.status === "content_stale" || d.status === "auditing") && !auto && (
        <div role="note" data-testid="ctrip-resubmit-off-note" className="rounded-[10px] border border-slate-200 bg-slate-50 px-3 py-2 text-[12.5px] text-slate-600">自动建档暂不启用，不能从这里重新提交；携程的审核结果仍会自动回来更新状态。要改内容请在携程 vbooking 后台改。</div>
      )}
      {(d.status === "rejected" || d.status === "content_stale") && canManage && (
        <div className="flex flex-wrap gap-2">
          {d.status === "rejected" && (() => { const t = fixTarget(d.audit?.reject_reason, productId); return <Link to={t.to} data-testid="ctrip-fix-link" className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-[12.5px] text-slate-700 hover:bg-slate-50">{t.text}</Link>; })()}
          {auto && <button type="button" disabled={resub.isPending} onClick={() => resub.mutate()} className="rounded-lg bg-blue-600 px-3 py-1.5 text-[12.5px] font-semibold text-white disabled:opacity-50">
            {resub.isPending ? "提交中…" : "改好了，重新提交"}
          </button>}
        </div>
      )}
      {err && <div role="alert" className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[12.5px] text-rose-700">{ctripErrorMessage(err)}</div>}
      {d.recent_pushes.length > 0 && (
        <table className="w-full text-[12.5px]">
          <thead><tr className="border-b border-slate-200 text-left text-slate-400"><th className="py-1.5 font-medium">最近推送</th><th className="font-medium">内容</th><th className="font-medium">时间</th><th className="font-medium">结果</th></tr></thead>
          <tbody>
            {d.recent_pushes.map((p, i) => (
              <tr key={i} className="border-b border-slate-100">
                <td className="py-1.5">{{ price: "价格", inventory: "余位", selling: "班期", listing: "建档" }[p.kind]}</td>
                <td>{p.summary}</td><td className="tabular-nums">{fmtTime(p.at)}</td>
                <td><span className={`rounded-full px-2 py-0.5 text-[11.5px] ${p.result === "ok" ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-600"}`}>{p.result === "ok" ? "成功" : "失败"}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="text-[11.5px] text-slate-400">失败的会自动重试，详细请求 / 返回在「渠道中心 › 携程 › 最近同步日志」。</div>
    </div>
  );
}

/** CTRIP-P3 · 「已在携程后台（vbooking）建好？」：填两个编码 → 绑定并立即推库存 + 开班期（样稿 ② 绑定块） */
function BindBlock({ optionId, canManage, onBound }: { optionId: string; canManage: boolean; onBound: () => void }) {
  const [vp, setVp] = useState("");
  const [vk, setVk] = useState("");
  const [cid, setCid] = useState("");
  const bind = useMutation({
    mutationFn: () => bindCtripListing(optionId, { vendor_product_code: vp.trim(), vendor_package_code: vk.trim(), ...(/^\d+$/.test(cid.trim()) ? { ctrip_product_id: Number(cid.trim()) } : {}) }),
    onSuccess: onBound,
  });
  const ready = vp.trim() !== "" && vk.trim() !== "";
  const cls = "w-full rounded-lg border border-slate-300 px-2.5 py-1.5 text-[13px] focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20";
  return (
    <div className="flex flex-col gap-3 rounded-[10px] border border-blue-200 bg-blue-50/40 px-4 py-3" data-testid="ctrip-bind-block">
      <div className="flex items-center gap-2 text-[13.5px] font-semibold text-slate-800">已在携程后台（vbooking）建好？<span className="rounded bg-violet-600 px-1.5 py-px text-[10px] font-bold text-white">10-29 新</span></div>
      <div className="grid gap-3 sm:grid-cols-3">
        <label className="flex flex-col gap-1 text-[12px] text-slate-500"><span className="font-medium text-slate-700">供应商产品编码<span className="text-rose-500">*</span></span>
          <input className={cls} data-testid="ctrip-bind-vp" value={vp} onChange={(e) => setVp(e.target.value)} disabled={!canManage} />
          <span>vbooking 产品配置第一页的「供应商产品编码」</span></label>
        <label className="flex flex-col gap-1 text-[12px] text-slate-500"><span className="font-medium text-slate-700">供应商套餐编码<span className="text-rose-500">*</span></span>
          <input className={cls} data-testid="ctrip-bind-vk" value={vk} onChange={(e) => setVk(e.target.value)} placeholder="LA-SF-AP-4C" disabled={!canManage} />
          <span>套餐管理页；1–4 人档都在这个套餐里</span></label>
        <label className="flex flex-col gap-1 text-[12px] text-slate-500"><span className="font-medium text-slate-700">携程产品 ID</span>
          <input className={cls} data-testid="ctrip-bind-cid" value={cid} onChange={(e) => setCid(e.target.value)} placeholder="79063772" disabled={!canManage} />
          <span>选填，只做显示</span></label>
      </div>
      {bind.isError && <div role="alert" data-testid="ctrip-bind-error" className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[12.5px] text-rose-700">{ctripErrorMessage(bind.error)}</div>}
      <div className="flex flex-wrap items-center gap-3">
        {canManage && (
          <button type="button" data-testid="ctrip-bind-submit" disabled={!ready || bind.isPending} onClick={() => bind.mutate()}
            className="rounded-lg bg-blue-600 px-3 py-1.5 text-[13px] font-semibold text-white disabled:cursor-not-allowed disabled:opacity-45">{bind.isPending ? "绑定中…" : "绑定并开始推库存班期"}</button>
        )}
        <span className="text-[11.5px] text-slate-500">绑定后立即推一次余位、开班期，以后自动推；携程从「不设库存」变成真实余位。</span>
      </div>
      <div className="text-[11.5px] text-slate-400">价格先在 vbooking 人工维护（携程接口暂不支持人档，等携程答复后再开自动推价，只推美元底价）。</div>
    </div>
  );
}

/** 手工绑定之后：在售 + 两个编码 + 最近推送 + 解绑（解绑只停推，不调携程下线） */
function Bound({ d, canManage, optionId, onChanged }: { d: CtripListingDetail; canManage: boolean; optionId: string; onChanged: () => void }) {
  const unbind = useMutation({ mutationFn: () => unbindCtripListing(optionId), onSuccess: onChanged });
  return (
    <div className="flex flex-col gap-3" data-testid="ctrip-bound">
      <div className="flex flex-wrap items-center gap-2 text-[13px]">
        <span className="rounded-full bg-emerald-50 px-2.5 py-0.5 font-semibold text-emerald-600">已绑定 · 自动推库存 / 班期</span>
        <span className="font-mono text-[12px] text-slate-600" data-testid="ctrip-bound-codes">{d.vendor_product_code} / {d.vendor_package_code}</span>
        {d.ctrip_product_code != null && <span className="font-mono text-[12px] text-slate-400">携程产品 ID {d.ctrip_product_code}</span>}
        {canManage && (
          <button type="button" data-testid="ctrip-unbind" disabled={unbind.isPending} onClick={() => { if (window.confirm("解绑后停止推库存 / 班期（不会在携程下线，携程上的余位停在最后一次）。确认？")) unbind.mutate(); }}
            className="ml-auto rounded-lg border border-slate-300 bg-white px-2.5 py-1 text-[12px] text-slate-700 disabled:opacity-50">解绑</button>
        )}
      </div>
      <div className="text-[12px] text-slate-500">最近一次余位推送：{fmtTime(d.last_inventory_push_at)}</div>
      {unbind.isError && <div role="alert" className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[12.5px] text-rose-700">{ctripErrorMessage(unbind.error)}</div>}
      {d.recent_pushes.length > 0 && (
        <table className="w-full text-[12.5px]">
          <thead><tr className="border-b border-slate-200 text-left text-slate-400"><th className="py-1.5 font-medium">最近推送</th><th className="font-medium">时间</th><th className="font-medium">结果</th></tr></thead>
          <tbody>{d.recent_pushes.map((p, i) => (
            <tr key={i} className="border-b border-slate-100"><td className="py-1.5">{{ price: "价格", inventory: "余位", selling: "班期", listing: "建档" }[p.kind]}</td><td className="tabular-nums">{fmtTime(p.at)}</td>
              <td><span className={`rounded-full px-2 py-0.5 text-[11.5px] ${p.result === "ok" ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-600"}`}>{p.result === "ok" ? "成功" : "失败"}</span></td></tr>
          ))}</tbody>
        </table>
      )}
      <div className="text-[11.5px] text-slate-400">失败的会自动重试，详细请求 / 返回在「渠道中心 › 携程 › 最近同步日志」。价格先在 vbooking 人工维护。</div>
    </div>
  );
}

export function CtripListingDrawer({
  optionId, productId, productName, optionCode, canManage, onClose, onSubmitted, onBindChanged,
}: { optionId: string; productId: string; productName: string; optionCode: string; canManage: boolean; onClose: () => void; onSubmitted: () => void; onBindChanged?: () => void }) {
  const qc = useQueryClient();
  const detailQ = useQuery({ queryKey: [...CTRIP_LISTING_KEY, "detail", optionId], queryFn: () => getCtripListing(optionId) });
  const bound = !!detailQ.data?.bound;                                  // CTRIP-P3 · 手工绑定（on_sale + 两个编码）
  const submitted = !!detailQ.data && !bound && !["missing", "draft"].includes(detailQ.data.status);
  const preQ = useQuery({
    queryKey: [...CTRIP_LISTING_KEY, "precheck", optionId], queryFn: () => precheckCtripListing(optionId),
    enabled: detailQ.isSuccess && !submitted, refetchInterval: 5000, refetchOnWindowFocus: true,   // 补齐后自动刷新，不用关抽屉
  });
  const refreshAll = () => { qc.invalidateQueries({ queryKey: CTRIP_LISTING_KEY }); onSubmitted(); };
  // CTRIP-P3 · 绑定 / 解绑：后端自己建 / 停映射，这里只刷新，不能走 onSubmitted（那条会再勾一次「推送」建一套住房档映射）
  const refreshBind = () => { qc.invalidateQueries({ queryKey: CTRIP_LISTING_KEY }); onBindChanged?.(); };
  const submit = useMutation({
    mutationFn: () => submitCtripListing(optionId),
    onSuccess: refreshAll,
    onError: () => qc.invalidateQueries({ queryKey: [...CTRIP_LISTING_KEY, "precheck", optionId] }),
  });
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const pre = preQ.data;
  const missing = pre?.missing ?? [];
  const status = detailQ.data?.status ?? "missing";
  const pill = STATUS_PILL[status] ?? STATUS_PILL.missing;
  const pillText = bound ? "已绑定 · 在售" : !submitted && pre ? (missing.length ? `缺 ${missing.length} 项` : "可提交") : pill.text;   // CTRIP-P3 · 手工绑定后不再显示预检缺项数
  const pillCls = bound ? "bg-emerald-50 text-emerald-600" : !submitted && pre && !missing.length ? "bg-emerald-50 text-emerald-600" : pill.cls;
  const oneDay = preQ.isError && /一日游/.test(ctripErrorMessage(preQ.error));

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/40" onClick={onClose} data-testid="ctrip-drawer-bg">
      <div className="flex h-full w-full max-w-[620px] animate-in slide-in-from-right flex-col bg-white shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="ctrip-drawer" role="dialog" aria-label="携程建档">
        <div className="border-b border-slate-200 px-5 py-4">
          <div className="flex flex-wrap items-center gap-2 text-[16px] font-semibold text-slate-900">
            {productName} <span className="font-mono text-[12px] font-normal text-slate-500">{optionCode}</span>
            <span className={`rounded-full px-2.5 py-0.5 text-[12px] font-medium ${pillCls}`} data-testid="ctrip-drawer-pill">{pillText}</span>
            <button type="button" aria-label="关闭" onClick={onClose} className="ml-auto text-[18px] font-normal text-slate-400 hover:text-slate-600">✕</button>
          </div>
          <div className="mt-1 text-[12px] text-slate-400">一个选项 = 携程一个产品 · 携程产品号提交后才有</div>
        </div>

        <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-5 py-4">
          {detailQ.isLoading && <div className="py-8 text-center text-[13px] text-slate-500">加载中…</div>}
          {detailQ.isError && <div role="alert" className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[12.5px] text-rose-700">{ctripErrorMessage(detailQ.error)}</div>}
          {detailQ.data && bound && <Bound d={detailQ.data} canManage={canManage} optionId={optionId} onChanged={refreshBind} />}
          {detailQ.data && !bound && !submitted && <BindBlock optionId={optionId} canManage={canManage} onBound={refreshBind} />}
          {detailQ.data && submitted && <Submitted d={detailQ.data} canManage={canManage} optionId={optionId} productId={productId} onChanged={refreshAll} />}
          {oneDay && <div role="alert" className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-[12.5px] text-slate-600">一日游走携程「玩乐」，未接入。</div>}
          {!submitted && preQ.isError && !oneDay && <div role="alert" className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[12.5px] text-rose-700">{ctripErrorMessage(preQ.error)}</div>}
          {!submitted && !bound && pre && (
            <>
              {detailQ.data && !detailQ.data.auto_listing_enabled && (
                <div role="note" data-testid="ctrip-selfcheck-note" className="rounded-[10px] border border-slate-200 bg-slate-50 px-3 py-2 text-[12.5px] text-slate-600">「提交到携程审核」（系统自动建档）暂不启用，下面的缺项清单当作上携程前的资料自查。</div>
              )}
              {GROUPS.map((g) => {
                const items = missing.filter((m) => m.group === g.key);
                return (
                  <div key={g.key} data-testid={`ctrip-group-${g.key}`}>
                    <h5 className="mb-1.5 text-[13px] font-semibold text-slate-600">{g.title}</h5>
                    <ul className="flex flex-col rounded-[10px] border border-slate-200">
                      {items.length === 0 && <li className="flex items-baseline gap-2.5 px-3 py-2 text-[13px]"><span className="w-4 font-bold text-emerald-600">✓</span><span className="text-slate-600">{g.okText}</span></li>}
                      {items.map((m, i) => (
                        <li key={`${m.field}-${i}`} className="flex flex-wrap items-baseline gap-2.5 border-b border-slate-100 px-3 py-2 text-[13px] last:border-b-0" data-testid="ctrip-missing-item">
                          <span className="w-4 font-bold text-rose-600">✕</span>
                          <b className="font-semibold text-slate-800">{m.label}</b>
                          <span className="min-w-[160px] flex-1 font-medium text-rose-600">{m.detail || "还没补"}</span>
                          {m.fix_link && <Link to={m.fix_link} className="text-[12.5px] font-medium text-blue-600 hover:underline">{g.key === "lookup" ? "去对照" : "去补"}</Link>}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
              {pre.warnings.map((w, i) => <div key={i} role="note" className="rounded-[10px] border border-amber-200 bg-amber-50 px-3 py-2 text-[12.5px] text-amber-800">{w}</div>)}
              <div>
                <h5 className="mb-1.5 text-[13px] font-semibold text-slate-600">推过去长这样</h5>
                <div className="overflow-x-auto rounded-[10px] border border-slate-200">
                  <table className="w-full min-w-[480px] text-[13px]" data-testid="ctrip-packages">
                    <thead><tr className="bg-slate-50 text-left text-[12px] text-slate-400"><th className="px-2.5 py-2 font-medium">携程套餐</th><th className="font-medium">编号</th><th className="font-medium">成人 卖价 / 底价</th><th className="font-medium">儿童</th></tr></thead>
                    <tbody>
                      {pre.packages.map((p) => (
                        <tr key={p.name} className="border-t border-slate-100">
                          <td className="px-2.5 py-2">{p.name}</td>
                          <td className="font-mono text-[12px] text-slate-600">{p.code || "（不带编号）"}</td>
                          <td className="tabular-nums">${p.adult_sale.toLocaleString("en-US")} / ${p.adult_cost.toLocaleString("en-US")}</td>
                          <td className="text-[12px] text-slate-400">= 成人</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="mt-1.5 text-[11.5px] text-slate-400">
                  美元 · 卖价 = 携程渠道挂牌价，底价 = 结算价 · 目前有 {pre.inventory_days} 个可售团期日；余位每天推 min(车位, 团期名额)，四个套餐推同一个数，满了推 0 不关班期。
                </div>
              </div>
            </>
          )}
          {submit.isError && (
            <div role="alert" data-testid="ctrip-submit-error" className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[12.5px] text-rose-700">
              {ctripErrorMessage(submit.error)}（已建的部分不会丢，补好后可直接重试）
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-slate-200 px-5 py-3">
          {!submitted && !bound && <span className="mr-auto text-[11.5px] text-slate-400">补齐后自动刷新，不用关抽屉</span>}
          <button type="button" onClick={onClose} className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-[13px] text-slate-700">关闭</button>
          {!submitted && !bound && canManage && detailQ.data?.auto_listing_enabled && (
            <button type="button" data-testid="ctrip-submit" disabled={!pre || missing.length > 0 || submit.isPending} title={missing.length ? `还缺 ${missing.length} 项` : undefined}
              onClick={() => submit.mutate()} className="rounded-lg bg-blue-600 px-3 py-1.5 text-[13px] font-semibold text-white disabled:cursor-not-allowed disabled:opacity-45">
              {submit.isPending ? "提交中…" : "提交到携程审核"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
